import { ProgressCircle } from '@react-spectrum/s2';
import { memo, useEffect, useMemo, useState } from 'react';

import { BlobImage } from '@/components/blob/BlobImage';

import { EMAIL_GENERATING_STATUS_LINES } from './panelMeta';
import { useEmailTemplates } from './useEmailTemplates';

const ROTATE_MS = 4000;

type EmailGeneratingInterstitialProps = {
	selectedTemplateId: string | null;
};

export const EmailGeneratingInterstitial = memo(
	function EmailGeneratingInterstitial({
		selectedTemplateId,
	}: EmailGeneratingInterstitialProps) {
		const { templates } = useEmailTemplates();
		const [statusIndex, setStatusIndex] = useState(0);

		useEffect(() => {
			const id = setInterval(() => {
				setStatusIndex(
					(prev) => (prev + 1) % EMAIL_GENERATING_STATUS_LINES.length,
				);
			}, ROTATE_MS);
			return () => clearInterval(id);
		}, []);

		const template = useMemo(() => {
			if (!selectedTemplateId) return null;
			return templates.find((t) => t.id === selectedTemplateId) ?? null;
		}, [selectedTemplateId, templates]);

		return (
			<div
				className="flex flex-1 flex-col items-center justify-center gap-6 p-6"
			>
				{template?.thumbnail_link ? (
					<div className="relative h-64 w-80 overflow-hidden rounded">
						<BlobImage
							src={template.thumbnail_link}
							alt={template.name ?? 'Selected template'}
							className="h-full w-full object-cover"
						/>
						<div
							className="absolute inset-0 flex items-center justify-center
								bg-black/40"
						>
							<ProgressCircle
								isIndeterminate
								size="L"
								aria-label="Generating email"
								staticColor="white"
							/>
						</div>
					</div>
				) : (
					<ProgressCircle
						isIndeterminate
						size="L"
						aria-label="Generating email"
					/>
				)}
				<p className="text-center text-base font-medium text-gray-500">
					{EMAIL_GENERATING_STATUS_LINES[statusIndex]}...
				</p>
			</div>
		);
	},
);
